import type { ShipperDocument } from '@khaddi/contracts';
import { Icon, Text, colors, radii } from '@khaddi/ui';
import { Pressable, View } from 'react-native';
import { PhotoInput } from './PhotoInput';

export const SIGNUP_STEPS = ['Thông tin', 'Giấy tờ', 'Nhận tiền'] as const;

export const StepIndicator = ({ step }: { step: number }) => (
  <View accessibilityRole="list" aria-label="Các bước đăng ký" style={{ flexDirection: 'row', gap: 6 }}>
    {SIGNUP_STEPS.map((label, index) => {
      const reached = index <= step;
      return (
        <View key={label} accessibilityRole="listitem" aria-current={index === step ? 'step' : undefined} style={{ flex: 1, gap: 6 }}>
          <View style={{ height: 6, borderRadius: 3, backgroundColor: reached ? colors.primary : colors.border }} />
          <Text variant="label" color={reached ? colors.text : colors.textMuted} style={{ fontSize: 12, fontWeight: reached ? '700' : '500' }}>
            {index + 1}. {label}
          </Text>
        </View>
      );
    })}
  </View>
);

export const DocumentTile = ({ id, label, value, onPicked }: { id: ShipperDocument; label: string; value: string | undefined; onPicked: (reference: string) => void }) => {
  const done = !!value;
  const prompt = id === 'portrait' ? 'Chụp selfie' : 'Chụp ảnh';
  return (
    <PhotoInput label={`${label}: ${done ? 'đã tải lên, chụp lại' : prompt}`} onPicked={onPicked} camera={id === 'portrait' ? 'user' : 'environment'} style={{ flex: 1, minWidth: 140 }}>
      <View
        style={{
          height: 92,
          borderRadius: radii.md,
          borderWidth: 1.5,
          borderStyle: 'dashed',
          borderColor: done ? colors.green : colors.sky,
          backgroundColor: done ? colors.greenSoft : colors.background,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        {done ? (
          <View style={{ width: 22, height: 22, borderRadius: 11, borderWidth: 2.4, borderColor: colors.green, alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="check" size={13} color={colors.green} strokeWidth={3.2} />
          </View>
        ) : (
          <Icon name="camera" size={22} color={colors.primaryText} />
        )}
        <Text variant="label" color={colors.text} style={{ fontSize: 12 }}>
          {label}
        </Text>
        <Text variant="label" color={done ? colors.green : colors.textMuted} style={{ fontSize: 11, fontWeight: '500' }}>
          {done ? 'Đã tải lên' : prompt}
        </Text>
      </View>
    </PhotoInput>
  );
};

export const ShiftButton = ({ label, time, selected, onPress }: { label: string; time: string; selected: boolean; onPress: () => void }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityState={{ selected }}
    aria-pressed={selected}
    accessibilityLabel={`Ca ${label} ${time}`}
    onPress={onPress}
    style={{
      flex: 1,
      height: 52,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: selected ? colors.primaryText : colors.border,
      backgroundColor: selected ? colors.surfaceMuted : colors.surface,
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Text variant="bodyStrong" style={{ fontSize: 13 }}>
      {label}
    </Text>
    <Text variant="label" style={{ fontSize: 11, fontWeight: '500' }}>
      {time}
    </Text>
  </Pressable>
);

export const TermsCheck = ({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) => (
  <Pressable accessibilityRole="checkbox" accessibilityState={{ checked }} onPress={() => onChange(!checked)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44 }}>
    <View style={{ width: 18, height: 18, borderRadius: 4, borderWidth: 2, borderColor: checked ? colors.primaryText : colors.inputBorder, backgroundColor: checked ? colors.primary : colors.surface, alignItems: 'center', justifyContent: 'center' }}>
      {checked ? <Icon name="check" size={12} color={colors.onPrimary} strokeWidth={3.5} /> : null}
    </View>
    <Text variant="caption">
      Tôi đồng ý với{' '}
      <Text variant="caption" color={colors.primaryText} style={{ textDecorationLine: 'underline' }}>
        quy định hợp tác giao hàng
      </Text>
    </Text>
  </Pressable>
);

export const FieldLabel = ({ children }: { children: string }) => (
  <Text variant="label" color={colors.text} style={{ fontSize: 13, marginBottom: 8 }}>
    {children}
  </Text>
);
